import { CACHE_CONFIG_TYPES, COOKIE_NAMES, STORE_NAME } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IUserRegistrationService } from '@intigral/domain/lib/application/services/UserRegistrationService';
import { setCookie } from '@intigral/utils/lib/browser-storage/cookie';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { getSubscriptionGuids } from '@intigral/utils/lib/user';
import { getConfiguration } from '../hooks/useConfiguration';
import DOMPurify from 'dompurify';
import users from '../actions/users';
import { appConfig } from '../config/app.config';
import c, {
    ItemPosition,
    KIDSMODE,
    PLATFORMS,
    possibleLanguages,
    ratingControls,
    REMINDER,
    VMX_DEVICE_TYPES,
    SCREEN_NAME_MAPPING,
} from '../config/constants';
import i18n, { __ } from '../translations';
import { IAppData } from '../types/IAppData';
import { ILocalStorageConfigurationObject } from '../types/ILocalStorage';
import apis from './apis';
import browser from './browser';
import { getOS } from './os';
import url from './url';
import user from './user';
import { deleteCookie as deleteCookieFn, setCookie as setCookiefn } from '@intigral/utils/lib/browser-storage/cookie';

export let appData: Partial<IAppData> = {};
// @ts-expect-error For testing purposes
window.appData = appData;

// const newConfig: ILocalStorageConfigurationObject = getConfiguration();

export const setReduxGlobal = (store) => {
    global.reduxState = store;
};

export const getReduxGlobal = () => {
    return global.reduxState;
};

const h = {
    getStoreItem: (key) => {
        if (!window.localStorage || !window.localStorage.getItem) {
            return null;
        }
        return window.localStorage.getItem(key);
    },
    setStoreItem: (key, value) => {
        if (!window.localStorage || !window.localStorage.setItem) {
            return null;
        }
        return window.localStorage.setItem(key, value);
    },
    getStore: (key) => {
        if (!window.localStorage || !window.localStorage.getItem) {
            return null;
        }
        return JSON.parse(window.localStorage.getItem(key));
    },
    setStore: (key, value) => {
        if (!window.localStorage || !window.localStorage.setItem) {
            return null;
        }
        return window.localStorage.setItem(key, JSON.stringify(value));
    },
    getSessionStore: (key) => {
        if (!window.sessionStorage || !window.sessionStorage.getItem) {
            return null;
        }
        return JSON.parse(window.sessionStorage.getItem(key));
    },
    setSessionStore: (key, value) => {
        if (!window.sessionStorage || !window.sessionStorage.setItem) {
            return null;
        }
        return window.sessionStorage.setItem(key, JSON.stringify(value));
    },
    deleteStore: (key) => {
        if (!window.localStorage || !window.localStorage.removeItem) {
            return null;
        }
        return window.localStorage.removeItem(key);
    },
    clearAllStore: () => {
        window.localStorage.clear();
    },
    clearStoreStatuses: () => {
        appConfig.loadStatusesStore.forEach((key) => {
            h.deleteStore(key);
        });
    },
    clearConfigurationCache: () => {
        // Clear configuration-related localStorage items
        //  Configuration now managed by React Query - no localStorage cleanup needed
        // h.deleteStore(CACHE_CONFIG_TYPES.configuration);
        h.deleteStore(CACHE_CONFIG_TYPES.baseConfiguration);
        h.deleteStore(CACHE_CONFIG_TYPES.version);
    },
    setContext(data) {
        appData = Object.assign(appData, data);
    },
    getAppData() {
        return appData;
    },
    getLang() {
        return appData.lang || 'en';
    },
    isEnglishLang() {
        return appData.lang === 'en' ? true : false;
    },
    setLang(config) {
        // check url locale and set Context
        const [...path] = window.location.pathname.split('/');
        const lang = Object.keys(config.languages).includes(path[1]) ? path[1] : appConfig.defaultLanguage;
        this.setContext({ lang });
        setCookie(COOKIE_NAMES.PREFERRED_LANGUAGE, lang, 365, true, true);
        this.setPageDirection(lang);
    },
    switchLang(lang) {
        this.setContext({ lang });
        setCookie(COOKIE_NAMES.PREFERRED_LANGUAGE, lang, 365, true, true);
        this.setPageDirection(lang);
        i18n.changeLanguage(lang);
        return true;
    },
    /**
     * Function to get Sorted user subscription guids
     * @returns {string|*}
     */
    getSortedSubscriptionGuid(feedType, config?: ILocalStorageConfigurationObject) {
        const userInfo = user.getUserInfo();
        // Use provided config or fallback to localStorage for backward compatibility
        const newConfig: ILocalStorageConfigurationObject = config;

        return encodeURIComponent(
            getSubscriptionGuids(feedType || 'mpg', userInfo, newConfig?.paidVsFree || {})
                .sort()
                .join('|')
        );
    },

    /**
     * Function to get allowed subscriptionGuids from CMS config
     * @returns {boolean|*}
     */
    getCmsGuidsConfig(configParam?: ILocalStorageConfigurationObject) {
        // Use provided config or fallback to localStorage for backward compatibility
        const config = configParam || getConfiguration();
        if (config?.subscriptionGuids?.length > 0) {
            return config?.subscriptionGuids;
        }
        return false;
    },
    getDevice() {
        return appConfig.device;
    },
    getDeviceType() {
        const deviceInfo = user.getDeviceInfo();
        return deviceInfo && deviceInfo.deviceType ? deviceInfo.deviceType : appConfig.deviceType;
    },
    getDeviceGuid() {
        const deviceInfo = user.getDeviceInfo();
        return deviceInfo && deviceInfo.deviceGuid ? deviceInfo.deviceGuid : appConfig?.deviceGuid;
    },
    getParentalRatingMappings(parentalRating, configParam?: ILocalStorageConfigurationObject) {
        // Use provided config or fallback to localStorage for backward compatibility
        const newConfig: ILocalStorageConfigurationObject = configParam || getConfiguration();

        // If parental rating is not set, return null
        if (!parentalRating) {
            return null;
        }

        // If there are no parental rating mappings, return the parental rating
        if (!newConfig?.recommendation?.parentalRatingMappings) {
            return parentalRating;
        }

        // Return the mapped parental rating
        // If the rating is not mapped, return the parental rating
        return newConfig?.recommendation?.parentalRatingMappings[parentalRating] ?? parentalRating;
    },
    getLocation() {
        return getReduxGlobal().getState().location.countryName
            ? getReduxGlobal().getState().location.countryName
            : null;
    },
    getUserIP() {
        return getReduxGlobal().getState().location.userIP || '';
    },
    getCountriesAllowed(configParam?: ILocalStorageConfigurationObject) {
        // Use provided config or fallback to localStorage for backward compatibility
        const config = configParam || getConfiguration();
        const countriesAllowed = config?.geolocationRestriction?.countriesAllowed;
        return countriesAllowed ? countriesAllowed : [];
    },
    getNotAllowedRoute(configParam?: ILocalStorageConfigurationObject) {
        // Use provided config or fallback to localStorage for backward compatibility
        const config = configParam || getConfiguration();
        const notAllowedScreen = config?.geolocationRestriction?.notAllowedScreen;
        return notAllowedScreen ? notAllowedScreen : '';
    },
    isAllowedCountry() {
        try {
            const userCountryCode = getCountry();
            const countriesAllowed = this.getCountriesAllowed();
            const notAllowedRoute = this.getNotAllowedRoute();

            // Safety checks to prevent issues during initialization
            if (!userCountryCode || !Array.isArray(countriesAllowed) || !notAllowedRoute) {
                return false;
            }

            if (!countriesAllowed.includes(userCountryCode) && !url.isCurrentRoute(notAllowedRoute)) {
                // Use setTimeout to prevent interrupting the current execution flow
                setTimeout(() => {
                    window.location.href = url.route(notAllowedRoute);
                }, 100);
            } else if (url.isCurrentRoute(notAllowedRoute) && countriesAllowed.includes(userCountryCode)) {
                // Use setTimeout to prevent interrupting the current execution flow
                setTimeout(() => {
                    window.location.href = url.route('home');
                }, 100);
            }
            return false;
        } catch (error) {
            return false;
        }
    },
    isAllowedForKids() {
        const config = getConfiguration();
        if (
            user.getProfileMode() === KIDSMODE.KID &&
            !['parental-pin-management', 'select-profile'].includes(url.getCurrentPageName())
        ) {
            const notAllowedRoutes = config?.kidsConfig?.notAllowedRoutes ?? [];
            if (notAllowedRoutes.includes(url.getCurrentPageName())) {
                window.location.href = url.route('kids-home');
            }
        }
    },
    isParentalPinScreen() {
        return url?.getCurrentPageName()?.includes('parental-pin-management');
    },
    isAccountRetrievalFeatureEnabled() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.kidsConfig?.accountRetrieval === true;
    },
    getParentalPinPossibleScreens(val) {
        const values = [
            'add',
            'change',
            'validate',
            'set',
            'account-retrieval-otp-generate',
            'account-retrieval-otp-validate',
            'account-retrieval-pin-view',
            'parental-lock-reset-otp',
            'parental-lock-reset-pin',
        ];
        return values.includes(val);
    },
    isAllowedMobileStreaming() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.device?.allowMobileStreaming || false;
    },
    isAllowedToAddMobileDevice() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.device?.addMobile || false;
    },

    getVMXDeviceTypes() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.device?.vmxDeviceType || VMX_DEVICE_TYPES;
    },
    getMigrationData() {
        const config = getConfiguration();
        return (
            config?.rebranding || {
                enableMigrationBanner: false,
            }
        );
    },
    getVMXDeviceName() {
        const vmxDeviceTypes = h.getVMXDeviceTypes();
        const name = browser.name();
        return vmxDeviceTypes[name];
    },
    getScreen: () => {
        if (appData.mixpanel && appData.mixpanel.screenName) {
            return appData.mixpanel.screenName;
        }
        // Get the current path
        const path = window.location.pathname;

        // Check if it's a provider route
        const providerMatch = /\/providers\?providerId=([^&]+)/.exec(window.location.href);
        if (providerMatch) {
            return providerMatch[1];
        }

        // Get the last part of the path
        const pathParts = path.split('/').filter((part) => part);
        const lastPath = pathParts[pathParts.length - 1];
        if (lastPath === undefined) {
            return 'Home';
        }
        // Return mapped name if exists, otherwise return the last path
        return SCREEN_NAME_MAPPING[lastPath] || lastPath;
    },
    getScreenMixpanelData: () => appData.mixpanel || {},
    getScreenMixpanelDataById: (screenId) => {
        const mixpanelPages = h.getMixPanelPages();
        return mixpanelPages[screenId] || {};
    },
    getScreenMixpanelPropsName() {
        if (appData.screenMetaData && appData.screenMetaData.mixpanel) {
            return appData.screenMetaData.mixpanel.pageprops;
        }
        return null;
    },
    getCarouselComponentType(type) {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.carouselComponentsTypes[type] || type;
    },
    isExcludedScreenFromMixpanelView() {
        if (
            appData.screenMetaData &&
            appData.screenMetaData.mixpanel &&
            appData.screenMetaData.mixpanel.isExcludeScreen
        ) {
            return appData.screenMetaData.mixpanel.isExcludeScreen === '1';
        }
        return false;
    },
    getAltLanguageURL(lang, location) {
        if (!location) {
            return '';
        }
        const [...path] = location.pathname
            .split('/')
            .filter((path) => path !== '' && !possibleLanguages.includes(path));
        if (lang !== appConfig.defaultLanguage) {
            path.unshift(lang);
        }
        return ('/' + path.join('/') + location.search).replace('//', '/');
    },
    getPageDirection: (lang: string) => {
        lang = lang ? lang : h.getLang();
        return lang === c.LANG_AR ? c.LANG_RTL : c.LANG_LTR;
    },
    setPageDirection: (lang) => {
        document.body.setAttribute('dir', h.getPageDirection(lang));
    },
    getDefaultRange: () => {
        return appConfig.defaultApiRange;
    },
    filterObject: (object, content = 'config', key = null) => {
        const config = getConfiguration();
        const filtered = content.split('.').reduce((o, p) => (o ? o[p] : null), config);
        if (key === null) {
            return filtered;
        }
        return filtered && filtered[key] ? filtered[key] : null;
    },
    setUserRatingValues(userParentalValue = appData.parentalControl) {
        const parentalControl = {};
        const items = Object.values(ratingControls);
        for (const i in items) {
            userParentalValue =
                userParentalValue === 'Family' || userParentalValue === 'F' ? 'FAMILY' : userParentalValue;
            if (userParentalValue === items[i]) {
                parentalControl[i] = items[i];
                break;
            } else {
                parentalControl[i] = items[i];
            }
        }
        return parentalControl;
    },
    isDeviceSupported() {
        const [osName, osVersion] = getOS(navigator.userAgent);
        const supportedOS = appConfig.osSupport;
        const osCondition = supportedOS.find((os) => os.name === osName);
        if (osCondition) {
            return osVersion >= osCondition.version;
        }
        return true;
    },
    isMobileAndTablet() {
        const platform = browser.os();
        return platform === PLATFORMS.UNKNOWN
            ? null
            : platform === PLATFORMS.ANDROID || platform === PLATFORMS.IPHONE || platform === PLATFORMS.IPAD;
    },
    getIndexOfArrayByAttrVal(array, attr, value) {
        for (let i = 0; i < array.length; i += 1) {
            if (array[i][attr] === value) {
                return i;
            }
        }
        return -1;
    },
    getTenantName() {
        const userInfo = user.getUserInfo();
        return userInfo.tenantName || 'NA';
    },
    getBookmarkSettings() {
        //  FIXED: Using React Query configuration instead of Redux
        const config = getConfiguration();
        return (
            config?.bookmarkSettings || {
                removalPercentage: 95,
                withDelete: false,
            }
        );
    },
    getSliderSettings() {
        //  FIXED: Using React Query configuration instead of Redux
        const config = getConfiguration();
        const { appConfig } = (config as any) || {};
        if (appConfig && 'sliderSettings' in appConfig) return appConfig.sliderSettings;
        else return {};
    },
    getPromotions() {
        const config = getConfiguration();
        return config?.promotions;
    },
    getExtraSubscriptions() {
        //  FIXED: Using React Query configuration instead of Redux
        const config = getConfiguration();
        return config?.extraSubscriptionsGuids || {};
    },
    getExtraSubscriptionsForUserSegment() {
        const userInfo = user.getUserInfo();
        const operator = (userInfo && 'operator' in userInfo && userInfo.operator.toUpperCase()) || null;
        const Subscriptions = this.getExtraSubscriptions();
        if (!Subscriptions) {
            return [];
        }
        const grantedSubscriptions = [];
        for (const [key, value] of Object.entries(Subscriptions)) {
            let grantedSubscription = [];
            if (Array.isArray(value)) {
                grantedSubscription = value.reduce((result, item) => {
                    if (item.toUpperCase() === operator) result.push(key);
                    return result;
                }, []);
            }
            grantedSubscriptions.push(grantedSubscription);
        }
        return grantedSubscriptions.flat();
    },
    getMixPanelKey() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.analytics?.mixpanel?.token || 'bd4db0e7a39f1511bb058cd4b2336274';
    },
    getYouboraKey() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.analytics.youbora.systemId || 'rockettvdev';
    },

    /**
     * Get product identifier from Youbora CMS config
     * @returns {string} Jawwy TV | stc TV
     */
    getYouboraIdentifier() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.analytics.youbora.youbora_product_identifier || 'stc TV';
    },

    getProviders: () => {
        const config = getConfiguration();
        return config?.analytics?.mixpanel?.providers || {};
    },
    getMixPanelPages: () => {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.analytics.mixpanel.pages || {};
    },
    getSearchAutoSuggestionConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return (
            newConfig?.search.autoSuggestion || {
                maxResultsOnUI: 5,
                startCharIndex: 3,
                stopWordIndex: 2,
            }
        );
    },
    getMaxRecentSearchCount() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.search.recentSearch.maxCount || 5;
    },
    getSeachBackCacheInMinute() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.search?.backCacheInMinute || 5;
    },
    getProvidersNameByMap: (vodProvider) => {
        const providers = h.getProviders();
        const providerName = Object.keys(providers).reduce((result, item) => {
            if (item.toUpperCase() === vodProvider.toUpperCase()) result.push(providers[item].mixpanel);
            return result;
        }, []);

        return providerName.toString();
    },
    getProvidersIconBaseUrl() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.images?.providers_base_url || '';
    },
    getPormotionBannerConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.promotionBanner || {};
    },
    getCookie: (cookieName) => {
        const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
        if (match) return match[2];
    },
    setCookie: (cname, cvalue, exdays, withSubDomain = false, isSecure = false) => {
        // const d = new Date();
        // cvalue = encodeURIComponent(cvalue);
        // d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
        // let expires = 'expires=' + d.toUTCString();
        // if (exdays === 0) {
        //     expires = '';
        // }
        // document.cookie =
        //     cname +
        //     '=' +
        //     cvalue +
        //     ';path=/;' +
        //     expires +
        //     (withSubDomain ? '; domain=' + appConfig.cookieDomain : '') +
        //     '; secure';
        setCookiefn(cname, cvalue, exdays, withSubDomain, isSecure);
    },
    deleteCookie: (cname, withSubDomain = false) => {
        // h.setCookie(cname, '', -1000, withSubDomain);
        deleteCookieFn(cname, withSubDomain);
    },
    getImagizerUrl(url = '', width = 0, height = 0, quality = 75) {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();
        if (!url) {
            return '';
        }
        if (url?.includes('http:')) {
            url = url.replace('http:', 'https:');
        }
        const imagizerQuality = newConfig?.imagizer?.imagizerQuality || quality || 75;
        const imagizerUrl = newConfig?.imagizer?.imagizerUrl;
        if (imagizerUrl) {
            return `${imagizerUrl}/imagizer/${width}x${height}@${imagizerQuality}/${url}`;
        }

        return url || '';
    },
    getTVodConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.tvodConfig;
    },
    getLearningConfig(type) {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.recommendation?.learning[type] || {};
    },
    getOperators() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.operators;
    },
    isTVODEnabled() {
        const userInfo = user.getUserInfo();
        const operators = this.getOperators();
        let userOperator = (userInfo && 'operator' in userInfo && userInfo.operator) || '';
        userOperator = userOperator.toLowerCase();
        const operatorsList = Object.keys(operators || {}).reduce((acc, operator) => {
            acc[operator.toLowerCase()] = operators[operator];
            return acc;
        }, {});
        //  FIXED: Using React Query configuration instead of appData
        const config = getConfiguration();
        const msisdnPrefix = config?.tvodConfig?.msisdnPrefix ?? [];
        const paidVsFreeIsEnabled = config?.paidVsFree?.lockIconEnable;
        const userOperatorTVODConfig = operatorsList[userOperator]?.tvod;
        if (
            !userOperatorTVODConfig?.enable ||
            (!user.checkTVODUserMsisdnValidity(msisdnPrefix) && paidVsFreeIsEnabled)
        ) {
            return false;
        }
        return true;
    },
    hasTVODTag(item) {
        // Check if 'tags' array exists and is an array
        if (!Array.isArray(item.tags)) {
            return false;
        }

        // Use 'some()' to check if at least one tag has the title "TVOD"
        return item.tags.some((tag) => tag.title === 'TVOD');
    },
    allowPlayerOnMobileConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();
        return Boolean(newConfig?.allowPlayerOnMobileDevice);
    },
    getSkipVideoConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        try {
            if (newConfig?.skipVideo?.enable) {
                return {
                    skipIntro: true,
                    skipOutro: true,
                };
            } else {
                return {
                    skipIntro: newConfig?.skipVideo?.skipIntro?.enable ?? false,
                    skipOutro: newConfig?.skipVideo?.skipOutro?.enable ?? false,
                };
            }
        } catch (e) {
            return {
                skipIntro: false,
                skipOutro: false,
            };
        }
    },
    getCDNConfig(config) {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        try {
            if (config) {
                return config?.cdn.broadpeak;
            } else {
                return newConfig?.cdn?.broadpeak;
            }
        } catch (e) {
            return;
        }
    },
    getSeriesGUIDfromSeriesID(url) {
        return url.substring(url.lastIndexOf('/') + 1);
    },
    getBucketIdFromAccountId(accountId) {
        return (accountId >>> 9) + (accountId & 0x1ff);
    },
    getAccountId() {
        return user.getAccountId();
    },
    isJsonString(str) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    },
    hasOwn: {}.hasOwnProperty,
    classNames(...args) {
        const classes = [];
        // for (let i = 0; i < arguments.length; i++) {
        //     const arg: any = arguments[i];
        //     if (!arg) continue;

        //     const argType = typeof arg;

        //     if (argType === 'string' || argType === 'number') {
        //         classes.push(arg);
        //     } else if (Array.isArray(arg) && arg.length) {
        //         const inner = h.classNames(...(arg as []));
        //         if (inner) {
        //             classes.push(inner);
        //         }
        //     } else if (argType === 'object') {
        //         for (const key in arg) {
        //             if (h.hasOwn.call(arg, key) && arg[key]) {
        //                 classes.push(key);
        //             }
        //         }
        //     }
        // }
        args.forEach((arg) => {
            if (!arg) return; // Skip falsy values (e.g., null, undefined, false)

            const argType = typeof arg;

            if (argType === 'string' || argType === 'number') {
                // Push string or number values directly
                classes.push(String(arg));
            } else if (Array.isArray(arg)) {
                // Recursively handle arrays by flattening them
                const inner = this.classNames(...arg);
                if (inner) classes.push(inner);
            } else if (argType === 'object') {
                // Handle objects (push keys where the value is truthy)
                Object.keys(arg).forEach((key) => {
                    if (arg[key]) {
                        classes.push(key);
                    }
                });
            }
        });
        return classes.join(' ');
    },
    async shouldUpdateConfiguration(refreshApp = false) {
        // get stored version and configuration object
        const version = this.getStoreItem(CACHE_CONFIG_TYPES.version);
        // getConfiguration version from akami
        let configVersionNew = '-1';
        try {
            configVersionNew = await apis.getCmsService().configurations().getAppConfigurationVersrion();
            this.setStoreItem(CACHE_CONFIG_TYPES.version, configVersionNew);
        } catch (e) {
            return false; // to prevent any exceptions if api key endpoint has an issue
        }
        if (version !== configVersionNew) {
            if (!refreshApp) {
                return true;
            } else {
                this.deleteStore(CACHE_CONFIG_TYPES.version);
                //  Configuration now managed by React Query - no localStorage deletion needed
                // this.deleteStore(CACHE_CONFIG_TYPES.configuration);
                self.location.reload();
            }
        } else if (refreshApp) {
            return true;
        }
        return false;
    },
    async getAndupdateConfiguration() {
        if (await this.shouldUpdateConfiguration()) {
            const NewConfiguration = await apis.getCmsService().configurations().getAppConfiguration();

            // mapping languages with allowed ones
            const allowedLanguages = NewConfiguration?.configuration?.appConfig?.allowedLanguages;

            const mappedLanguages = Object.fromEntries(
                Object.entries(NewConfiguration.languages).filter(([lang]) => allowedLanguages.includes(lang))
            );

            NewConfiguration.languages = mappedLanguages;

            // Removed localStorage caching - configuration is now managed by React Query
            // this.setStoreItem(CACHE_CONFIG_TYPES.configuration, JSON.stringify(NewConfiguration));
            return NewConfiguration;
        } else {
            // No longer reading from localStorage - configuration should be fetched fresh
            // Fallback: return empty configuration object
            return {};
        }
    },
    async getBaseConfiguration() {
        const result = await apis.getCmsService().configurations().getBaseConfiguration();
        this.setStoreItem(CACHE_CONFIG_TYPES.baseConfiguration, JSON.stringify(result));
    },
    /**
     * it is function that return all user object and Backend configuration
     * with exclude pre-defined keys from config.json under element called
     * "excludedUserParams"
     * @param {string} url to append query on
     * @returns {string} full url with query ready to call
     */
    getJawwy2Parameters(url?: string) {
        const userParameters = { ...user.getUserInfo() };
        const excludedParameters = appConfig.excludedUserParams;
        if (userParameters && typeof userParameters === 'object') {
            Object.keys(userParameters).forEach((key) => {
                if (
                    excludedParameters.includes(key) ||
                    userParameters[key] === '' ||
                    (typeof userParameters[key] === 'object' && userParameters[key] === null) ||
                    (typeof userParameters[key] === 'object' &&
                        userParameters[key] &&
                        typeof Object.keys(userParameters[key]) === 'undefined') ||
                    (typeof userParameters[key] === 'object' &&
                        userParameters[key] &&
                        typeof Object.keys(userParameters[key]) !== 'undefined' &&
                        Object.keys(userParameters[key]).length === 0)
                ) {
                    delete userParameters[key];
                }
                /* cannot convert parameters to lowercase since some of them are case sensitive */
                // else if (typeof userParameters[key] === "string") {
                //     userParameters[key] = userParameters[key].toLowerCase();
                // }
            });
        }
        if (typeof url === 'undefined') {
            url = ''; // Replace 'your_default_url_here' with your actual default URL.
        }

        const configParameters =
            appConfig.baseConfigs && typeof appConfig.baseConfigs.be_apis_params === 'object'
                ? appConfig.baseConfigs.be_apis_params
                : {};

        Object.assign(userParameters, configParameters);

        const queryParams = [];
        Object.keys(userParameters).forEach((k) => {
            queryParams.push(`${k}=${encodeURIComponent(userParameters[k])}`);
        });
        const queryString = queryParams.join('&');

        // Now 'url' is guaranteed to be defined, so this check is safe.
        url = `${queryString}`;

        return url;
    },
    isIncluded: (arr, val) => {
        return arr.includes(val);
    },
    numberOfReminders: () => {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.reminder?.reminderLimit || REMINDER.REMINDER_LIMIT;
    },
    reminderCutoffTime: () => {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.reminder?.reminderCutoffTimeMins || REMINDER.REMINDER_CUTOFF_TIME;
    },
    getProductKey: () => {
        return appConfig.productKey;
    },
    getContentFilteredQueryWithSubscriptionsPlusExtraGuids() {
        const subscriptionGuids = decodeURIComponent(getGuid()).split('|') || [];
        const extraGuids = h.getExtraSubscriptionsForUserSegment();
        const finalSubscriptionGuids =
            Array.isArray(subscriptionGuids) && Array.isArray(extraGuids)
                ? [...subscriptionGuids, ...extraGuids]
                : subscriptionGuids;
        return finalSubscriptionGuids.sort().join('|');
    },
    getContentFilteredQueryWithoutDeviceType() {
        return getGuid();
    },
    getOpenAppDeepLinkUrl() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.tvodConfig?.openAppDeepLinkUrl;
    },
    getOpenAppDeepLinkUrlPPV() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.ppvConfigV2?.openAppDeepLinkUrl;
    },
    qrCodePurchaseEnable() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.tvodConfig?.qrCodePurchaseEnable || false;
    },
    getAppDeepLinkUrlTvodRent() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig?.gamificationConfigs?.openAppDeepLinkUrlTvodRent;
    },
    getAppDeepLinkUrlTierUpgrade() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();
        return newConfig?.gamificationConfigs?.openAppDeepLinkUrlTierUpgrade;
    },
    isLiveLikeProfile() {
        const profile = get(STORE_NAME.SELECTED_PROFILE);
        return Boolean(profile?.liveLikeProfileId || null);
    },
    getGenreRating(data) {
        if (data && Array.isArray(data)) {
            const config = getConfiguration();
            if (!config.genreRating) {
                return null;
            }
            let rating;
            const { newRatingScheme, oldRatingScheme } = config.genreRating;

            const newScheme = newRatingScheme !== undefined ? newRatingScheme : 'urn:simple';
            const oldScheme = oldRatingScheme !== undefined ? oldRatingScheme : 'urn:www.citc.gov.sa';

            // Check for new rating "urn:simple" scheme first
            const simpleScheme = data.find((item) => item.scheme === newScheme);
            if (simpleScheme) {
                rating = simpleScheme.rating;
            } else {
                // If "urn:simple" scheme doesn't exist, check for old rating "urn:www.citc.gov.sa" scheme
                const citcScheme = data.find((item) => item.scheme === oldScheme);
                if (citcScheme) {
                    rating = citcScheme.rating;
                } else {
                    // If neither scheme exists, set rating to empty string
                    rating = '';
                }
            }

            return rating;
        } else {
            return '';
        }
    },
    getAppConfig() {
        const newConfig: ILocalStorageConfigurationObject = getConfiguration();

        return newConfig;
    },

    getUserConsent: async () => {
        const { apiKey, identityToken, authToken } = h.getStore(STORE_NAME.USER) || {};
        if (!apiKey || !authToken || !identityToken) {
            throw new Error('Missing user credentials for consent');
        }
        const userConsent = await container
            .resolve<IUserRegistrationService>(DI.SERVICES.IUserRegistrationService)
            .getConsent(apiKey, authToken, identityToken);
        return userConsent;
    },
    showConsentScreen: async (config: ILocalStorageConfigurationObject) => {
        const {
            consentVersion,
            waitTime: waitTimeInDays,
            enabledV2,
            consentQuestions,
        } = config?.consentManagement || {};

        if (enabledV2 === false) return false;

        if (!consentVersion || !waitTimeInDays || enabledV2 === undefined || !consentQuestions) {
            return false;
        }

        const user = h.getStore(STORE_NAME.USER) || {};
        if (!user.apiKey || !user.authToken || !user.identityToken) {
            return false;
        }

        const { consentVersion: getConsentVersion, lastConsentTsMs, consents } = await h.getUserConsent();
        if (getConsentVersion === undefined || consentVersion > getConsentVersion) {
            return true;
        }

        if (getConsentVersion === consentVersion) {
            const currentTimestamp = Date.now();
            const waitTimeInMs = waitTimeInDays * 86400000; // Convert days to milliseconds
            if (!lastConsentTsMs || waitTimeInMs < currentTimestamp - lastConsentTsMs) {
                return h.areAllConsentsAcknowledged(consents, consentQuestions) ? false : true;
            }
        }
        return false;
    },
    areAllConsentsAcknowledged: (userConsent, configConsentQuestions) => {
        // Check if the userConsent object contains both mandatory and optional fields
        if (!userConsent.mandatory || !userConsent.optional) {
            return false;
        }

        // Iterate over all consent questions in the configConsentQuestions object
        for (const question of configConsentQuestions) {
            const questionId = question.id;
            const isMandatory = question.isMandatory;

            // Check if the question exists in the response
            if (isMandatory) {
                if (!userConsent.mandatory[questionId] || userConsent.mandatory[questionId] !== true) {
                    return false; // Return false if any mandatory question is missing or not true
                }
            } else {
                if (userConsent.optional[questionId] === undefined || userConsent.optional[questionId] !== true) {
                    return false; // Return false if any optional question is not true
                }
            }
        }

        // If all checks passed, return true
        return true;
    },
    fetchPageContent: async (url) => {
        try {
            const res = await fetch(url);

            // Check if the response is in JSON format
            const contentType = res.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                const data = await res.json();
                return { data, loading: false, error: false };
            } else {
                const textData = await res.text(); // If it's not JSON, assume it's text/HTML
                return { data: textData, loading: false, error: false };
            }
        } catch (error) {
            return { data: null, loading: false, error: true };
        }
    },
};

export const getCountry = () => {
    const store = getReduxGlobal().getState();
    try {
        const userContext = user.getUserEnvironmentContext();
        //  FIXED: Using React Query configuration instead of Redux
        const config = getConfiguration();
        if (userContext && config?.geolocationRestriction?.overwrite?.includes(userContext)) {
            return appConfig.defaultCountryCode;
        }
        return store.location.countryCode ? store.location.countryCode : appConfig.defaultCountryCode;
    } catch (e) {
        return null;
    }
};

export const redirectToProfileSelection = (code) => {
    const parsedProfile = JSON.parse(h.getStoreItem('selectedProfile'));
    switch (code) {
        case '9005': // fall-through (OR-ing)
        case '9006':
        case '9503':
        case '9100': // new invalid error code
            // only refresh if you logged out from user-status and came back again, then selectedProfile is invalid, so a clearing and refresh is required
            if (
                h.getStoreItem('selectedProfile') !== '{}' &&
                parsedProfile !== null &&
                'profileGuid' in parsedProfile
            ) {
                h.setStoreItem('selectedProfile', '{}');
                window.location.reload();
            }
            break;
        case '9001':
            h.setStoreItem('selectedProfile', '{}');
            break;
        default:
            break;
    }
};

/**
 *
 * @param {*} code
 * force logout the user in case of the defined error codes
 * 9933 => access restricted for the product
 */
export const forceLogout = (code) => {
    if (user.getUserInfo()?.isGuest === true) {
        return;
    }
    switch (code) {
        case '9933':
            users.callTrack(code, 'Forcelogout - 9933');
            users.logoutUser(true);
            break;
        default:
            break;
    }
};

export const getStoredBaseConfiguration = () => {
    return JSON.parse(h.getStoreItem(CACHE_CONFIG_TYPES.baseConfiguration));
};

export const getSliderItemClasses = (index, focusedItemIndex, focusedItemPosition) => {
    return {
        'transform-first': focusedItemIndex === index && focusedItemPosition === ItemPosition.First,
        'mid-item': focusedItemIndex === index && focusedItemPosition === ItemPosition.Mid,
        'transform-last': focusedItemIndex === index && focusedItemPosition === ItemPosition.Last,
        'transform-first-after':
            focusedItemIndex !== null && focusedItemIndex < index && focusedItemPosition === ItemPosition.First,
        'transform-last-before':
            focusedItemIndex !== null && focusedItemIndex < index && focusedItemPosition === ItemPosition.Last,
        'transform-before':
            focusedItemIndex !== null && focusedItemIndex < index && focusedItemPosition === ItemPosition.Mid,
        'transform-first-before':
            focusedItemIndex !== null && focusedItemIndex > index && focusedItemPosition === ItemPosition.First,
        'transform-last-after':
            focusedItemIndex !== null && focusedItemIndex > index && focusedItemPosition === ItemPosition.Last,
        'transform-after':
            focusedItemIndex !== null && focusedItemIndex > index && focusedItemPosition === ItemPosition.Mid,
    };
};

export const getAllSliderItemClasses = () => {
    return {
        'transform-first': true,
        'mid-item': true,
        'transform-last': true,
        'transform-first-after': true,
        'transform-last-before': true,
        'transform-before': true,
        'transform-first-before': true,
        'transform-last-after': true,
        'transform-after': true,
    };
};

export const sanitizeText = (text, owsap = true) => {
    if (!DOMPurify.isSupported) return text;
    //Sanitize the text against XSS attack
    try {
        text = DOMPurify.sanitize(text);
    } catch (e) {
        return '';
    }
    //As per OWASP recommendatin on encoding input to prevent attacks
    if (!owsap) return text;
    return text
        .replace(/&/g, '') //&amp;
        .replace(/</g, '') //&lt;
        .replace(/>/g, '') //&gt;
        .replace(/"/g, ''); //&quot;
};
export const createInlineStyleSheet = (css, elementId) => {
    const element = document.createElement('style');
    element.setAttribute('id', elementId);
    element.appendChild(document.createTextNode(css));
    document.getElementsByTagName('head')[0].appendChild(element);
};
export const removeInlineStyleSheet = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) element.parentNode.removeChild(element);
};

export const isValidCSS = (cssContent) => {
    // this will return true if content has any html tags
    const hasTag = RegExp.prototype.test.bind(/(<([^>]+)>)/i);
    return !hasTag(cssContent);
};

/**
 * helper function to filter by key and multiple possibilities
 * @param {object} obj Object to filter
 * @param {string} key Key of object to filter by
 * @param  {...any} possibilities
 * @returns {boolean} if object has one of possibilities
 */
export const filterBy = (obj, key, ...possibilities) => {
    if (possibilities.length === 0 || !possibilities[0]) {
        return true;
    }
    return possibilities.includes(obj[key]);
};

export const isTVodProgram = (program) => {
    const tags = Array.isArray(program.programTags)
        ? program.programTags
        : Array.isArray(program.tags)
        ? program.tags
        : null;
    if (!tags) {
        return false;
    }
    return (
        tags.filter((tag) => {
            return tag.scheme === 'Categories' && tag.title === 'TVOD';
        }).length >= 1
    );
};

export const getVodProgramPurchaseStatus = (program) => {
    const purchaseStatus =
        typeof program === 'object' && program && 'purchaseStatus' in program ? program.purchaseStatus : null;
    if (!purchaseStatus) {
        return false;
    }
    return typeof purchaseStatus === 'object' && 'isRented' in purchaseStatus ? purchaseStatus.isRented : false;
};

/**
 * Get braze config from CMS
 * @returns {object}
 */
export const getBrazeData = () => {
    const config = getConfiguration();
    return (
        config?.braze || {
            apiKey: '',
            enableBraze: false,
            configParams: {},
        }
    );
};

/**
 * Function to get user subscription guids
 * @returns {string|*}
 */
export const getGuid = () => {
    const userInfo = user.getUserInfo();
    const userSubscriptionGuids = h.getCmsGuidsConfig();
    const config = getConfiguration();
    if (userSubscriptionGuids) {
        return encodeURIComponent(userSubscriptionGuids.join('|'));
    }

    if (userInfo && typeof Array.isArray(userInfo.subscriptionGUID) && userInfo.subscriptionGUID.length > 0) {
        return encodeURIComponent(getSubscriptionGuids('mpx', userInfo, config?.paidVsFree || {}).join('|'));
    } else {
        return encodeURIComponent(appConfig.defaultSubscription);
    }
};

export const getTAGuid = () => {
    const userInfo = user.getUserInfo();
    const userSubscriptionGuids = h.getCmsGuidsConfig();
    if (userSubscriptionGuids) {
        return userSubscriptionGuids.join(',');
    }
    if (userInfo && typeof Array.isArray(userInfo.subscriptionGUID) && userInfo.subscriptionGUID.length > 0) {
        return userInfo.subscriptionGUID.sort().join(',');
    } else {
        return appConfig.defaultSubscription;
    }
};

export const pluck = (array, key) => {
    return array.map((o) => o[key]);
};

export const pluckWithTranslation = (array, key) => {
    return array.map((o) => __(o[key]));
};

export const getLearnActionConfig = () => {
    const config = getConfiguration();
    return config?.learnAction || null;
};

export const getNewContentRating = (): string[] => {
    const config: ILocalStorageConfigurationObject = getConfiguration();
    return config?.profile?.rating?.map((ratingData) => ratingData?.rating) || [];
};

export const mapOldRatingToNewRating = (rating: string) => {
    return (
        {
            F: 'G',
            FAMILY: 'G',
            G: 'G',

            PG: 'PG',
            PG12: 'PG12',
            PG15: 'PG15',

            15: 'R15',
            R15: 'R15',

            18: 'R18',
            R18: 'R18',
        }[rating?.toUpperCase()] ||
        rating?.toUpperCase() ||
        rating ||
        ''
    );
};
export const mapNewRatingToOldRating = (rating) => {
    return {
        G: 'Family',
        PG: 'PG',
        PG12: '15',
        PG15: '15',
        R15: '18',
        R18: 'R18',
    }[rating];
};

export const getProfileRating = () => {
    const selectedProfile = user.getProfile();
    const parentalControl = selectedProfile?.newParentalControl || selectedProfile?.parentalControl;
    if (parentalControl) {
        if (parentalControl.toLowerCase() === 'f') return 'Family';
        return parentalControl;
    }
    return 'G'; // setting default to family as per the api documentation
};

export const getAccumulatedSubscriptionGuids = () => {
    const subscriptionGuids = decodeURIComponent(getGuid()).split('|') || [];
    const extraGuids = h.getExtraSubscriptionsForUserSegment();
    const finalSubscriptionGuids =
        Array.isArray(subscriptionGuids) && Array.isArray(extraGuids)
            ? [...subscriptionGuids, ...extraGuids]
            : subscriptionGuids;
    return finalSubscriptionGuids.sort().join('|');
};

export const shouldShowParentalRatingPopup = (ratings) => {
    const itemRating: string = Array.isArray(ratings) ? h.getGenreRating(ratings) : ratings?.toUpperCase();
    const currentProfileRating: string = user.getProfile()?.newParentalControl?.toUpperCase();

    if (itemRating && currentProfileRating) {
        // An array of rating, like this: ["G", "PG", "PG12", "PG15", "R15", "R18"]
        const contentRatingArray = getNewContentRating();
        // If the current profile rating has higher/equal index then it can play the content.
        if (
            contentRatingArray.indexOf(currentProfileRating) < contentRatingArray.indexOf(itemRating?.toUpperCase()) &&
            h.isDeviceSupported()
        ) {
            return true;
        }
    }
    return false;
};

export const getFooterLink = (url: string, params) => {
    const config = getConfiguration() as any;
    const currentLang = h.getLang();
    const lang = currentLang === appConfig?.defaultLanguage ? '' : currentLang;
    if (params) {
        return lang
            ? `${config?.footer?.stcBaseUrl}${lang}/${url}?${params}`
            : `${config?.footer?.stcBaseUrl}${lang}/${url}?${params}`;
    } else {
        return lang ? `${config?.footer?.stcBaseUrl}${lang}/${url}` : `${config?.footer?.stcBaseUrl}${lang}/${url}`;
    }
};

export const getAppVersion = () => {
    const config = getConfiguration() as any;
    return config?.appReleaseVersion ?? appConfig.version;
};

export const getAppName = () => {
    return appConfig?.appName || 'STCTV_WCP_WEB';
};

export const safelyDecodeBase64 = (value: string) => {
    try {
        return JSON.parse(atob(value));
    } catch (error) {
        return null;
    }
};

export function getAvodConfiguration() {
    return (getConfiguration() as ILocalStorageConfigurationObject).avodConfigs;
}

/**
 * Builds a fully qualified URL with language prefix and query parameters.
 *
 * @param {string} path - The path to append after the language code (e.g., 'account', 'home').
 * @param {Record<string, string | number | boolean>} [params={}] - An object representing query parameters to append to the URL.
 * @returns {string} The constructed URL as a string.
 *
 * @example
 * // If the language is 'en' and the current origin is 'https://local.jawwy.tv:3080':
 * buildFullURL('account', { foo: 'bar', page: 2 });
 * // Returns: 'https://local.jawwy.tv:3080/en/account?foo=bar&page=2'
 */
export function buildFullURL(path: string, params: Record<string, string | number | boolean> = {}): string {
    const url = new URL(`/${h.getLang()}/${path}`, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
    });
    return url.toString();
}

export default h;
